import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import App from "./App.tsx";
import "./index.css";
import { AuthProvider } from "./contexts/AuthContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import { AgentProvider } from "./contexts/AgentContext";
import { FeatureGatingProvider } from "./contexts/FeatureGatingContext";
import { initSentry } from "./config/sentry";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Initialize Sentry before app renders
initSentry();

// TanStack Query configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Import useAuth after AuthProvider is defined
import { useAuth } from "./contexts/AuthContext";
import { BrowserRouter as Router } from "react-router-dom";

// Wrapper component that integrates OrganizationProvider with AuthProvider
function OrganizationProviderWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useAuth();

  // Debug logging

  // Only provide organization context if user has an organization_id
  const organizationId = auth.userProfile?.organization_id;

  return (
    <OrganizationProvider organizationId={organizationId} enableRealtime={true}>
      <AgentProvider>
        {organizationId && (
          <FeatureGatingProvider organizationId={organizationId}>
            {children}
          </FeatureGatingProvider>
        )}
        {!organizationId && children}
      </AgentProvider>
    </OrganizationProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <OrganizationProviderWrapper>
              <App />
            </OrganizationProviderWrapper>
          </AuthProvider>
        </Router>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
